import React, {ReactNode} from "react";
import {Checkbox} from "primereact/checkbox";
import {InputText} from "primereact/inputtext";
import FileHelper from "../../shared/helper/FileHelper";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import Downloader from "../document/Downloader";
import {Button} from "primereact/button";
import Parameter from "../dialog/parameter/Parameter";
import {RenameDialogDefinition} from "../dialog/components/document/RenameDialog";
import Exception from "../../shared/exception/Exception";
import ErrorCode from "../../shared/exception/ErrorCode";
import DomHelper from "../../shared/helper/DomHelper";
import {Tooltip} from "primereact/tooltip";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import DocumentInfo from "../document/components/DocumentInfo";
import DocumentSettings from "../document/DocumentSettings";
import SidebarRightMode from "../viewer/settings/SidebarRightMode";
import SidebarLeftMode from "../viewer/settings/SidebarLeftMode";
import {PdfaDialogDefinition} from "../dialog/components/convert/PdfaDialog";
import {DeleteDocumentType} from "../../shared/models/DeleteDocumentType";
import Immutable from "../../shared/immutable/Immutable";
import {SortType} from "../document/DocumentSorting";
import {PortalDocumentManagerGlobals} from "../document/PortalDocumentManager";
import {PortalGlobals} from "../Portal";
import Translate from "../../shared/translation/Translate";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Preview from "./Preview";
import MessageBox from "../../shared/components/MessageBox";
import {MessageType} from "../../shared/log/MessageInterface";
import Icon from "../../shared/components/Icon";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

interface DocumentTableItemProperties extends I18nGlobals, PortalGlobals, PortalDocumentManagerGlobals, DebugGlobals {
	portalDocument?: Immutable<PortalDocumentInterface>;
	filtered: boolean;
	sortBy: SortType;
}

interface DocumentTableItemState {
	renameFileName: string;
	renameDocumentId?: string;
	numberOfPages: number;
}

export class DocumentTableItemDefinition extends PortalPureComponent<DocumentTableItemProperties, DocumentTableItemState> {

	constructor(props: Readonly<DocumentTableItemProperties>) {
		super(props);

		this.state = {
			renameFileName: "",
			numberOfPages: 0
		};

		this.handleRename = this.handleRename.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	private async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		let numberOfPages: number = await portalDocument.getNumberOfPages();

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	public async handleRename(): Promise<void> {
		let portalDocument: PortalDocument | undefined;

		if (FunctionHelper.exists(this.state.renameDocumentId)) {
			portalDocument = this.props.context.documentManager.findDocument(this.state.renameDocumentId!)
		}

		if (FunctionHelper.exists(this.state.renameDocumentId) && FunctionHelper.exists(portalDocument) &&
			portalDocument!.getDocumentFile()?.fileName !== this.state.renameFileName) {
			try {
				await this.props.context.documentManager.renameDocument(
					this.state.renameDocumentId!, this.state.renameFileName
				);
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.handleRename();
				}
			}
		}

		this.setState({
			renameFileName: "",
			renameDocumentId: undefined
		});
	}

	/**
	 * Handles keyboard key down events
	 */
	private async handleKeydown(e: KeyboardEvent): Promise<void> {
		switch (e.key.toLowerCase()) {
			case "enter":
				e.stopPropagation();

				await this.handleRename();
				break;
			case "escape":
				e.stopPropagation();

				this.setState({
					renameFileName: "",
					renameDocumentId: undefined
				});
				break;
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.setNumberOfPages();

		window.addEventListener('click', this.handleRename);
		window.addEventListener('keydown', this.handleKeydown);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<DocumentTableItemProperties>, prevState: Readonly<DocumentTableItemState>, snapshot?: any
	): Promise<void> {
		await this.setNumberOfPages();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('click', this.handleRename);
		window.removeEventListener('keydown', this.handleKeydown);
	}

	/**
	 * render component as react node
	 */
	render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let itemId: string = DomHelper.generateUUID();
		let renameControlId = "control-rename-" + itemId;
		let downloader: Downloader = new Downloader(this.props.context.session.getToken()!);
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let executionError: Exception | undefined = portalDocument.getExecutionError();

		let error: undefined | Exception;
		if (FunctionHelper.exists(portalDocument.getDocumentFile()?.error)) {
			error = Exception.parseExceptionBean(portalDocument.getDocumentFile()!.error!);
		}

		let hasError: boolean = FunctionHelper.exists(error) && error?.code !== 0 &&
			DocumentInfo.hiddenMessages.indexOf(
				FunctionHelper.applyValue(error?.code, 0)
			) === -1;

		let classList: Array<string> = ["table-item"];
		if (!portalDocument.isLocked()) {
			classList.push("preview-" + itemId);
		}

		if (this.props.portalDocument!.get().settings.selected) {
			classList.push("selected")
		}

		return <>
			<tr className={classList.join(" ")} style={this.props.filtered ? {display: "none"} : {}}
				data-pr-position={"top"}
				onDoubleClick={(event) => {
					event.stopPropagation();

					if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
						return;
					}

					this.setState({
						renameFileName: "",
						renameDocumentId: undefined
					});

					this.props.context.documentManager.open(this.props.portalDocument!.get().id!);
				}}
				onClick={(event) => {
					if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
						return;
					}
					let documentSettings: DocumentSettings = portalDocument.getSettings();

					if (!event.shiftKey && !event.ctrlKey) {
						this.props.context.documentManager.select(
							this.props.portalDocument!.get().id!, true
						);
						return;
					}

					if (!documentSettings.selected) {
						this.props.context.documentManager.select(
							this.props.portalDocument!.get().id!, false
						);
					} else {
						this.props.context.documentManager.unselect(
							this.props.portalDocument!.get().id!
						);
					}
				}}>
				<td className="column-select"
					onClick={(event) => {
						event.stopPropagation();
					}}>
					<Checkbox checked={this.props.portalDocument!.get().settings.selected}
							  onChange={() => {
								  if (!FunctionHelper.exists(this.props.portalDocument) ||
									  !FunctionHelper.exists(this.props.portalDocument!.value)) {
									  return;
								  }

								  if (this.props.portalDocument!.get().settings.selected) {
									  this.props.context.documentManager.unselect(
										  this.props.portalDocument!.get().id!
									  );
								  } else {
									  this.props.context.documentManager.select(
										  this.props.portalDocument!.get().id!
									  );
								  }
							  }}/>
				</td>
				<td className={"column-name" + (this.props.sortBy === SortType.NAME ? " sorted" : "")}>
					{(!this.props.documentChecks || RenameDialogDefinition.isEnabled(this.props.context.info)) &&
                        <InputText value={this.state.renameFileName} id={renameControlId}
                                   style={
									   this.state.renameDocumentId ===
									   this.props.portalDocument!.get().id ? {} : {opacity: 0}
								   }
                                   onClick={(event) => {
									   if (!FunctionHelper.exists(this.props.portalDocument) ||
										   !FunctionHelper.exists(this.props.portalDocument!.value)) {
										   return;
									   }

									   this.setState({
										   renameFileName: this.props.portalDocument!.get().name,
										   renameDocumentId: this.props.portalDocument!.get().id
									   }, () => {
										   let renameInput: HTMLInputElement = event.target as HTMLInputElement;
										   renameInput.focus();
										   renameInput.select();
									   });

									   event.stopPropagation();
								   }}
                                   onChange={(event) => {
									   this.setState({
										   renameFileName: (event.target as HTMLInputElement).value
									   });
								   }}/>
					}
					<span className="filename">{this.props.portalDocument!.get().name}</span>
					<span className="fileextension">.{this.props.portalDocument!.get().extension}</span>
				</td>
				<td className={"column-archive" + (this.props.sortBy === SortType.ARCHIVE ? " sorted" : "")}
					onClick={(event: React.MouseEvent) => {
						event.stopPropagation(); // stop document selection
						this.props.context.documentManager.open(this.props.portalDocument?.get().id!);

						if (PdfaDialogDefinition.isActive(
							[portalDocument], this.props.context.info, this.props.signatureEditProfile
						)) {
							this.props.context.getPage().setState({
								showPdfaDialog: true,
								pdfaParameter: undefined
							});
						}
					}}>
					{portalDocument.isArchive() && !portalDocument.isPortfolio() &&
                        <Button className={this.props.sortBy === SortType.ARCHIVE ? " sorted" : ""}
                                tooltip={Translate.value(
									"startscreen.panels.documentmanager.archive", {ns: "screen"}
								)}>
							{this.props.portalDocument!.get().archiveType}
                        </Button>
					}
				</td>
				<td className={"column-signature" + (this.props.sortBy === SortType.SIGNATURE ? " sorted" : "")}
					onClick={(event) => {
						let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
							this.props.portalDocument?.get().id!
						);

						if (!portalDocument.hasSignature()) {
							return;
						}

						event.stopPropagation(); // stop document selection
						let documentSettings: DocumentSettings = portalDocument.getSettings();
						documentSettings.sidebarLeftMode = SidebarLeftMode.SIGNATURES;
						this.props.context.documentManager.open(this.props.portalDocument?.get().id!);
					}}>
					{portalDocument.hasSignature() && !portalDocument.isPortfolio() &&
                        <Button className={this.props.sortBy === SortType.SIGNATURE ? " sorted" : ""}
                                tooltip={Translate.value(
									"startscreen.panels.documentmanager.signature", {ns: "screen"}
								)}>
							{this.props.portalDocument!.get().signatures}
                        </Button>
					}
				</td>
				<td className={"column-password" + (this.props.sortBy === SortType.PASSWORD ? " sorted" : "")}
					onClick={(event) => {
						let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
							this.props.portalDocument?.get().id!
						);

						if (!portalDocument.hasPasswordProtection() && !portalDocument.hasCertificateProtection()) {
							return;
						}

						event.stopPropagation(); // stop document selection
						let documentSettings: DocumentSettings = portalDocument.getSettings();
						documentSettings.sidebarRightMode = SidebarRightMode.SECURITY;
						this.props.context.documentManager.open(this.props.portalDocument?.get().id!);
					}}>
					{(portalDocument.hasPasswordProtection() || portalDocument.hasCertificateProtection()) &&
                        <Button icon={portalDocument.hasPasswordProtection() ? "icon icon-password" :
							portalDocument.hasCertificateProtection() ? "icon icon-certificate" : ""}
                                className={this.props.sortBy === SortType.PASSWORD ? " sorted" : ""}
                                tooltip={
									portalDocument.hasPasswordProtection() ?
										Translate.value(
											"startscreen.panels.documentmanager.columns.security.password",
											{ns: "screen"}
										) :
										portalDocument.hasCertificateProtection() ?
											Translate.value(
												"startscreen.panels.documentmanager.columns.security.certificate",
												{ns: "screen"}
											) : undefined
								}/>
					}
				</td>
				<td className={"column-attachment" + (this.props.sortBy === SortType.ATTACHMENT ? " sorted" : "")}
					onClick={(event) => {
						if (FunctionHelper.applyValue(
							this.props.portalDocument?.get().attachments, 0
						) <= 0) {
							return;
						}

						event.stopPropagation(); // stop document selection
						let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
							this.props.portalDocument?.get().id!
						);

						if (!portalDocument.isPortfolio()) {
							let documentSettings: DocumentSettings = portalDocument.getSettings();
							documentSettings.sidebarLeftMode = SidebarLeftMode.ATTACHMENTS;
						}

						this.props.context.documentManager.open(this.props.portalDocument?.get().id!);
					}}>
					{this.props.portalDocument!.get().attachments > 0 && !portalDocument.isPortfolio() &&
                        <Button className={this.props.sortBy === SortType.ARCHIVE ? " sorted" : ""}
                                tooltip={Translate.value(
									"startscreen.panels.documentmanager.attachment", {ns: "screen"}
								)}>
							{this.props.portalDocument!.get().attachments}
                        </Button>
					}
				</td>
				<td className={"column-size" + (this.props.sortBy === SortType.SIZE ? " sorted" : "")}>
					{FileHelper.formatFileSize(FunctionHelper.applyValue(
						this.props.portalDocument!.get().size, 0
					), 2)}
				</td>
				<td className="column-error">
					{FunctionHelper.exists(executionError) &&
                        <Icon icon="icon-warning" autoHide={false} tooltipStyle={{maxWidth: "600px"}} position={"left"}
                              tooltip={
								  <MessageBox {...this.props} type={MessageType.DANGER}
											  stacktrace={
												  <>
													  <p>
														  {executionError?.message} (errorcode: {executionError?.code},
														  exitcode: {executionError?.exitCode})
													  </p>
													  <pre>{executionError?.stack}</pre>
												  </>
											  }>
									  {executionError?.message}
								  </MessageBox>
							  }/>
					}
				</td>
				<td className="column-functions">
					<Button className="button-view" icon="icon icon-eye" data-pr-position={"left"}
							onClick={(event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection

								if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
									return;
								}

								this.props.context.documentManager.open(this.props.portalDocument!.get().id!);
							}}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.columns.functions.view", {ns: "screen"}
							)}/>
					<Button className="button-delete" icon="icon icon-delete small" data-pr-position={"left"}
							disabled={portalDocument.isLocked()}
							onClick={(event: React.MouseEvent) => {
								if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
									return;
								}

								event.stopPropagation(); // stop document selection
								let parameter: Parameter<DeleteDocumentType> = new Parameter<DeleteDocumentType>(
									DeleteDocumentType.fromJson({
										documentIds: [this.props.portalDocument!.get().id!]
									} as DeleteDocumentType)
								);

								this.props.context.getPage().setState({
									showDeleteDialog: true,
									deleteParameter: parameter
								});
							}}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.columns.functions.delete",
								{ns: "screen"}
							)}/>
					<Button className="button-download" icon="icon icon-download small" data-pr-position={"left"}
							disabled={portalDocument.isLocked()}
							onClick={async (event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection

								if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
									return;
								}

								await downloader.downloadDocument(
									this.props.portalDocument!.get().id!,
									this.props.portalDocument!.get().fileName
								);
							}}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.columns.functions.download",
								{ns: "screen"}
							)}/>
				</td>
				<td className={"column-info" + (hasError ? " danger" : "")}>
					<Button className={"button-info info-" + itemId} data-pr-position={"left"}
							icon={"icon" + (hasError ? " icon-danger" : " icon-info")}
							onClick={(event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection

								let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
									this.props.portalDocument?.get().id!
								);
								let documentSettings: DocumentSettings = portalDocument.getSettings();
								documentSettings.sidebarRightMode = SidebarRightMode.INFO;
								this.props.context.documentManager.open(this.props.portalDocument?.get().id!);
							}}/>
				</td>
				{
					portalDocument.isPortfolioFile() &&
                    <Tooltip target={".preview-" + itemId} autoHide={false} className="tooltip-preview"
                             appendTo={document.getElementById("root")}>
                        <div className="tooltip-content large viewable">
                            <Preview {...this.props} controls={true} portalDocument={this.props.portalDocument!}/>
                        </div>
                    </Tooltip>
				}
				<Tooltip target={".info-" + itemId} appendTo={document.getElementById("root")}>
					<div className="document-info">
						<DocumentInfo {...this.props}/>
					</div>
				</Tooltip>
			</tr>
		</>;
	}
}

const DocumentTableItem = React.memo(DocumentTableItemDefinition,
	(
		prevProps: Readonly<DocumentTableItemProperties>,
		nextProps: Readonly<DocumentTableItemProperties>
	) => {
		if (!FunctionHelper.exists(nextProps.portalDocument)) {
			return true;
		}

		if (!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (prevProps.filtered !== nextProps.filtered) {
			return false;
		}

		if (prevProps.sortBy !== nextProps.sortBy) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		return true;
	}
);

export default DocumentTableItem;